import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Page, PageHead, PageTitle, PageSub } from '../components/ui/Page';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { Kicker } from '../components/ui/Kicker';
import { Icon, IconName } from '../components/ui/Icon';

interface ServiceItem {
  icon: IconName;
  title: string;
  desc: string;
  price: string;
  badge?: string;
}

const SERVICES: ServiceItem[] = [
  {
    icon: 'package',
    title: 'Standard parcels',
    desc: 'Documents and boxes up to 30 kg. 24+ carriers, live rates, labels in seconds.',
    price: 'from $4.80',
  },
  {
    icon: 'plane',
    title: 'Express international',
    desc: 'Next-day to 50+ countries. DHL, FedEx, UPS, Aramex priority services with full tracking.',
    price: 'from $24.00',
  },
  {
    icon: 'warehouse',
    title: 'Freight & LTL',
    desc: 'Pallets, skids, and full truckload. Dimensional pricing with transparent quotes.',
    price: 'Quote on request',
  },
  {
    icon: 'refresh',
    title: 'Returns',
    desc: 'Branded return portals for your customers. Pay-on-scan so you only pay for used labels.',
    price: 'from $3.50',
  },
  {
    icon: 'truck',
    title: 'Same-day & local',
    desc: '2-hour delivery in Ramallah and select US metros. Book via API or dashboard.',
    price: 'from $12.00',
  },
  {
    icon: 'bot',
    title: 'AI shipment',
    desc: 'Paste an invoice — our AI fills forms, picks carrier, books. Save 6 min per shipment.',
    price: 'Included',
    badge: 'NEW',
  },
];

const ServicesPage: React.FC = () => {
  const navigate = useNavigate();

  return (
    <Page>
      <PageHead>
        <div>
          <Kicker>Services</Kicker>
          <PageTitle style={{ marginTop: 8, fontSize: 40 }}>
            Ship anywhere. We handle the rest.
          </PageTitle>
          <PageSub style={{ fontSize: 15, maxWidth: 620 }}>
            From solo sellers to enterprise teams. Domestic, cross-border, freight, and returns — on
            one platform.
          </PageSub>
        </div>
      </PageHead>

      <div className="grid-3" style={{ marginTop: 16 }}>
        {SERVICES.map((s) => (
          <Card key={s.title} className="card-hover" style={{ padding: 24 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 10,
                  background: 'var(--bg-tint)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Icon name={s.icon} size={20} />
              </div>
              {s.badge && <Badge tone="cyan">{s.badge}</Badge>}
            </div>
            <h3 style={{ fontSize: 17, marginTop: 12 }}>{s.title}</h3>
            <p className="muted" style={{ fontSize: 14, marginTop: 6, lineHeight: 1.55 }}>{s.desc}</p>
            <div
              style={{
                marginTop: 14,
                paddingTop: 14,
                borderTop: '1px solid var(--line)',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <span className="mono" style={{ fontSize: 13, fontWeight: 600 }}>{s.price}</span>
              <Button variant="ghost" style={{ fontSize: 13 }}>
                Learn more <Icon name="arrowRight" size={13} />
              </Button>
            </div>
          </Card>
        ))}
      </div>

      {/* Enterprise CTA */}
      <div
        className="card"
        style={{
          padding: 36,
          marginTop: 32,
          background: 'var(--brand-navy)',
          color: '#fff',
          border: 'none',
          textAlign: 'center',
        }}
      >
        <h3 style={{ fontSize: 24, color: '#fff' }}>Shipping over 500 packages/month?</h3>
        <p style={{ marginTop: 8, color: '#cfd1e4' }}>
          Get custom rates, dedicated ops, and API support.
        </p>
        <Button
          onClick={() => navigate('/contact')}
          style={{ marginTop: 20, background: 'var(--brand-cyan)', color: 'var(--brand-navy)' }}
        >
          Talk to sales <Icon name="arrowRight" size={15} />
        </Button>
      </div>
    </Page>
  );
};

export default ServicesPage;
